import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Grid, Button, TextField, MenuItem,
  ToggleButtonGroup, ToggleButton, Checkbox, Chip, Divider, Tooltip,
  IconButton, LinearProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import {
  Groups as GroupIcon, Print as PrintIcon, Description as WordIcon,
  Sms as SmsIcon, Visibility as PreviewIcon,
  Leaderboard as AnalysisIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import {
  REPORT_CSS, reportCardHTML, classAnalysisHTML, openPrint, downloadWord,
} from './reportCardHtml';

const EXAM_TYPES = [
  { v: 'terminal', label: 'Terminal' },
  { v: 'mock',     label: 'Mock' },
  { v: 'mid',      label: 'Mid-Term' },
];

export default function GroupReportingPage() {
  const [lookups, setLookups] = useState({ classes: [], terms: [], acc_years: [] });
  const [cla, setCla]         = useState('');
  const [examType, setExamType] = useState('terminal');
  const [term, setTerm]       = useState('');
  const [acc_yer, setAccYer]  = useState('');
  const [examNum, setExamNum] = useState('');

  const [reports, setReports] = useState([]);
  const [picked, setPicked]   = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(false);

  // Preview dialog
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    API.get('/examination/lookups').then(r => setLookups(l => ({ ...l, ...r.data }))).catch(() => {});
  }, []);

  const canLoad = !!(cla && term && acc_yer && (examType === 'terminal' || examNum));

  const load = async () => {
    if (!canLoad) { swal.warning('Pick all the scope fields first'); return; }
    setLoading(true);
    try {
      const r = await API.get('/examination/reports/class', {
        params: { cla, exam_type: examType, term, acc_yer, exam_num: examNum },
      });
      setReports(r.data || []);
      setPicked(new Set((r.data || []).map(d => d.student?.index_no).filter(Boolean)));
    } catch (e) { swal.error(swal.pickError(e, 'Load failed')); }
    finally { setLoading(false); }
  };

  const toggle = (ind) => {
    setPicked(s => { const n = new Set(s); n.has(ind) ? n.delete(ind) : n.add(ind); return n; });
  };
  const all  = () => setPicked(new Set(reports.map(r => r.student?.index_no).filter(Boolean)));
  const none = () => setPicked(new Set());

  const selectedReports = useMemo(() =>
    reports.filter(r => picked.has(r.student?.index_no))
  , [reports, picked]);

  // The class on one ranked page, rather than 40 cards to read it off.
  // Prints the TICKED learners, so a head can analyse one stream or the
  // whole class without re-loading anything.
  //
  // Landscape, and `@page` is document-wide in print CSS — so this opens
  // its own print window instead of being appended to a run of portrait
  // report cards, which would turn those sideways too.
  const printAnalysis = () => {
    if (!selectedReports.length) { swal.warning('Tick at least one student'); return; }
    const html = classAnalysisHTML(selectedReports, {
      exam_type: examType, cla, term, acc_yer, exam_num: examNum,
    });
    openPrint(html, `Analysis-${cla}-${term}-${acc_yer}`);
  };

  const printAll = () => {
    if (!selectedReports.length) { swal.warning('Tick at least one student'); return; }
    const html = selectedReports.map((d, i) => {
      const sheet = reportCardHTML(d);
      return i < selectedReports.length - 1 ? sheet + '<div class="page-break"></div>' : sheet;
    }).join('');
    openPrint(html, `Reports-${cla}-${term}-${acc_yer}`);
  };

  const wordAll = () => {
    if (!selectedReports.length) { swal.warning('Tick at least one student'); return; }
    const html = selectedReports.map((d, i) => {
      const sheet = reportCardHTML(d);
      return i < selectedReports.length - 1 ? sheet + '<div class="page-break"></div>' : sheet;
    }).join('');
    const fn = `Reports-${cla}-${term}-${acc_yer}.doc`.replace(/\s+/g, '_');
    downloadWord(html, fn, fn);
  };

  const smsAll = async () => {
    if (!selectedReports.length) { swal.warning('Tick at least one student'); return; }
    const ok = await swal.confirm({
      title: `Queue SMS for ${selectedReports.length} parent(s)?`,
      text: 'Each student\'s parent gets a short results SMS queued into sms_send. Students with no parent number on file are skipped.',
      tone: 'warning', confirmText: 'Yes, queue all',
    });
    if (!ok) return;
    setBusy(true);
    let queued = 0, skipped = 0;
    for (const d of selectedReports) {
      const msg =
        `${d.student.fullname} — ${d.scope.term} (${d.scope.acc_yer}) ${d.scope.exam_type.toUpperCase()} report: ` +
        `Total ${Number(d.totals?.total || 0).toFixed(1)}, Position ${d.totals?.position || '—'}, Aggregate ${d.totals?.aggregate ?? '—'}.`;
      try {
        await API.post('/examination/reports/sms', { ind: d.student.index_no, message: msg });
        queued++;
      } catch { skipped++; }
    }
    setBusy(false);
    swal.success(`SMS queue: ${queued} queued · ${skipped} skipped (no number)`);
  };

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <ModuleHeader
        icon={<GroupIcon />}
        title="Group Reporting"
        subtitle="Batch report cards for an entire class — preview each, print as one document, or queue parent SMS"
      />

      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.4 }}>
          <Typography fontWeight={800}>Exam type</Typography>
          <ToggleButtonGroup size="small" exclusive value={examType} onChange={(_, v) => v && setExamType(v)}>
            {EXAM_TYPES.map(t => (
              <ToggleButton key={t.v} value={t.v} sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}>{t.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField select fullWidth size="small" label="Class" value={cla} onChange={e => setCla(e.target.value)}>
              <MenuItem value=""><em>— Pick —</em></MenuItem>
              {lookups.classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4} md={1.8}>
            <TextField select fullWidth size="small" label="Term" value={term} onChange={e => setTerm(e.target.value)}>
              <MenuItem value=""><em>— Pick —</em></MenuItem>
              {lookups.terms.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4} md={1.8}>
            <TextField select fullWidth size="small" label="Year" value={acc_yer} onChange={e => setAccYer(e.target.value)}>
              <MenuItem value=""><em>— Pick —</em></MenuItem>
              {lookups.acc_years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Grid>
          {examType !== 'terminal' && (
            <Grid item xs={6} sm={3} md={1.6}>
              <TextField fullWidth size="small" label={examType === 'mock' ? 'Mock number' : 'Mid-Term number'}
                value={examNum} onChange={e => setExamNum(e.target.value)} />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2.4}>
            <Button fullWidth variant="contained" onClick={load} disabled={!canLoad || loading}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              {loading ? <CircularProgress size={16} /> : 'Load class reports'}
            </Button>
          </Grid>
        </Grid>

        {reports.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.4, alignItems: 'center' }}>
            <Chip size="small" label={`${reports.length} students`} sx={{ fontWeight: 700 }} />
            <Chip size="small" label={`${picked.size} ticked`} color="primary" sx={{ fontWeight: 700 }} />
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={all}  disabled={!reports.length} sx={{ textTransform: 'none', fontWeight: 700 }}>All</Button>
            <Button size="small" onClick={none} disabled={!picked.size}    sx={{ textTransform: 'none', fontWeight: 700 }}>None</Button>
          </Stack>
        )}
      </Card>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button startIcon={<PrintIcon />} variant="contained" disabled={!selectedReports.length || busy}
          onClick={printAll} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Print all ({selectedReports.length})
        </Button>
        <Button startIcon={<AnalysisIcon />} variant="contained" color="secondary"
          disabled={!selectedReports.length || busy}
          onClick={printAnalysis} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Print analysis
        </Button>
        <Button startIcon={<WordIcon />} variant="outlined" disabled={!selectedReports.length || busy}
          onClick={wordAll} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Word ({selectedReports.length})
        </Button>
        <Button startIcon={<SmsIcon />} variant="outlined" disabled={!selectedReports.length || busy}
          onClick={smsAll} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Queue SMS to parents
        </Button>
      </Stack>

      {busy && <LinearProgress sx={{ mb: 1 }} />}

      <Card>
        {reports.length === 0 && !loading && (
          <Box sx={{ p: 4 }}>
            <Alert severity="info" variant="outlined">
              Pick scope above and click <b>Load class reports</b>.
            </Alert>
          </Box>
        )}
        {reports.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11 } }}>
                  <TableCell sx={{ width: 60 }}>Pick</TableCell>
                  <TableCell sx={{ width: 60 }}>#</TableCell>
                  <TableCell>Index</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell align="right">Subjects</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Position</TableCell>
                  <TableCell align="right">Aggregate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" sx={{ width: 70 }}>Preview</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((r, i) => {
                  const ind = r.student?.index_no;
                  return (
                    <TableRow key={ind || i} hover sx={{ '& td': { fontSize: 12.5, py: 0.4 } }}>
                      <TableCell><Checkbox size="small" checked={picked.has(ind)} onChange={() => toggle(ind)} /></TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{ind || '—'}</TableCell>
                      <TableCell>{r.student?.fullname || ''}</TableCell>
                      <TableCell align="right">{r.subjects?.length || 0}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.totals?.total)}</TableCell>
                      <TableCell align="right">{r.totals?.position || '—'}</TableCell>
                      <TableCell align="right">{r.totals?.aggregate ?? '—'}</TableCell>
                      <TableCell>
                        {r.error
                          ? <Chip size="small" color="warning" label="Missing data" sx={{ height: 20, fontWeight: 700 }} />
                          : r.conduct?.class_status
                            ? <Chip size="small" color="success" label={r.conduct.class_status} sx={{ height: 20, fontWeight: 700 }} />
                            : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Preview report">
                          <IconButton size="small" onClick={() => setPreviewData(r)}>
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={!!previewData} onClose={() => setPreviewData(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Preview — {previewData?.student?.fullname}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#eef0f4' }}>
          {previewData && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Box sx={{ bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <style>{REPORT_CSS}</style>
                <div dangerouslySetInnerHTML={{ __html: reportCardHTML(previewData) }} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => previewData && openPrint(reportCardHTML(previewData), `Report-${previewData.student?.fullname || ''}`)}
            startIcon={<PrintIcon />} variant="contained" sx={{ textTransform: 'none', fontWeight: 800 }}>
            Print this one
          </Button>
          <Button onClick={() => setPreviewData(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
